import { useLingui } from '@lingui/react';
import { Plural, Trans } from '@lingui/react/macro';
import { CalendarPlus, LayoutGrid, List, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProjectList } from '@/api/hooks';
import type { ListProjectsParams } from '@/api/projects';
import { CreateProjectModal } from '@/components/CreateProjectModal';
import { ModelPreviewImage } from '@/components/ModelPreviewImage';
import { SimulationStateBadge } from '@/components/SimulationStateBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { ProjectBasicInfo, SimulationState } from '@/types/api';
import { cn } from '@/utils/cn';
import { absoluteTime, relativeTime } from '@/utils/time';

const PAGE_SIZE = 20;

type ViewMode = 'tiles' | 'table';
type SortOption = 'created_at_desc' | 'created_at_asc' | 'name_asc' | 'name_desc';

const SIM_STATES: SimulationState[] = ['incomplete', 'ready', 'running', 'complete_ok', 'complete_collisions', 'error'];

const ProjectTile = ({ project }: { project: ProjectBasicInfo }): React.ReactElement => {
  const { i18n } = useLingui();
  return (
    <Link to={`/projects/${project.id}`} className="block">
      <Card className={cn('pt-0 overflow-hidden transition-all relative hover:shadow-lg')}>
        <SimulationStateBadge
          state={project.simulation_state}
          simulationResult={project.simulation_result}
          className="absolute top-4 right-4"
        />
        <ModelPreviewImage modelId={project.target_model?.id} alt={project.name} className="h-32 w-full" />
        <CardHeader>
          <CardTitle className="truncate" title={project.name}>
            {project.name}
          </CardTitle>
        </CardHeader>
        <div>
          <CardContent>
            {project.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {project.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="bg-blue-50 text-blue-600 hover:bg-blue-100">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
          <CardFooter>
            <div
              className="flex items-center gap-1 text-sm text-muted-foreground"
              title={absoluteTime(i18n, project.created_at)}
            >
              <CalendarPlus className="size-4" />
              <div>
                <Trans>
                  <span className="font-semibold">Created:</span> {relativeTime(i18n, project.created_at)}
                </Trans>
              </div>
            </div>
          </CardFooter>
        </div>
      </Card>
    </Link>
  );
};

/**
 * Project list page – tile/table view with filters and pagination.
 * Matches the UX design layout exactly.
 */
export const ProjectList = (): React.ReactElement => {
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('tiles');
  const [stateFilter, setStateFilter] = useState<SimulationState | ''>('');
  const [collisionFilter, setCollisionFilter] = useState<string>('');
  const [sortOption, setSortOption] = useState<SortOption>('created_at_desc');
  const { i18n } = useLingui();

  const sortBy = sortOption.startsWith('name') ? 'name' : 'created_at';
  const sortOrder = sortOption.endsWith('asc') ? 'asc' : 'desc';

  const listParams: ListProjectsParams = {
    page,
    size: PAGE_SIZE,
    simulationState: stateFilter ? [stateFilter] : undefined,
    hasCollisions: collisionFilter === 'with' ? true : collisionFilter === 'without' ? false : undefined,
    sortBy,
    sortOrder,
  };

  const { data, isLoading, isError, refetch } = useProjectList(listParams);
  const projects = data?.items ?? [];
  const total = data?.total ?? 0;

  const totalPages = Math.ceil(total / PAGE_SIZE);
  // Range shown in pagination: "Showing X–Y of Z"
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  const handleStateFilterChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setStateFilter(e.target.value as SimulationState | '');
    setPage(1);
  };

  const handleCollisionFilterChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setCollisionFilter(e.target.value);
    setPage(1);
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setSortOption(e.target.value as SortOption);
    setPage(1);
  };

  const selectClassName = 'select';

  const selectStyle = {
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M3 5l3 3 3-3' fill='none' stroke='%2394a3b8' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
  };

  return (
    <div className="fade-in mx-auto max-w-5xl p-2 sm:p-6">
      {/* Toolbar row – matches UX design exactly */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="mr-2 text-xl font-bold text-slate-800">
          <Trans>Projects</Trans>
        </h1>

        <div className="flex-1" />

        {/* Sort */}
        <select className={selectClassName} style={selectStyle} value={sortOption} onChange={handleSortChange}>
          <option value="created_at_desc">
            <Trans>Newest</Trans>
          </option>
          <option value="created_at_asc">
            <Trans>Oldest</Trans>
          </option>
          <option value="name_asc">
            <Trans>Name A–Z</Trans>
          </option>
          <option value="name_desc">
            <Trans>Name Z–A</Trans>
          </option>
        </select>

        {/* State filter */}
        <select className={selectClassName} style={selectStyle} value={stateFilter} onChange={handleStateFilterChange}>
          <option value="">
            <Trans>All states</Trans>
          </option>
          {SIM_STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        {/* Collision filter */}
        <select
          className={selectClassName}
          style={selectStyle}
          value={collisionFilter}
          onChange={handleCollisionFilterChange}
        >
          <option value="">
            <Trans>All</Trans>
          </option>
          <option value="with">
            <Trans>With collisions</Trans>
          </option>
          <option value="without">
            <Trans>Without collisions</Trans>
          </option>
        </select>

        {/* Vertical separator */}
        <div className="h-6 w-px bg-slate-200" />

        {/* View toggle – icon buttons */}
        <ToggleGroup
          variant="outline"
          value={[viewMode]}
          onValueChange={(value) => value[0] && setViewMode(value[0] as ViewMode)}
        >
          <ToggleGroupItem value="tiles" aria-label="Tiles view">
            <LayoutGrid />
          </ToggleGroupItem>
          <ToggleGroupItem value="table" aria-label="Table view">
            <List />
          </ToggleGroupItem>
        </ToggleGroup>

        {/* Vertical separator */}
        <div className="h-6 w-px bg-slate-200" />

        <CreateProjectModal
          trigger={
            <Button type="button">
              <Plus data-icon="inline-start" /> <Trans>New project</Trans>
            </Button>
          }
        />
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="py-12 text-center text-slate-500">
          <Trans>Loading…</Trans>
        </div>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <div className="py-12 text-center">
          <p className="mb-3 text-red-600">
            <Trans>Failed to load data.</Trans>
          </p>
          <Button type="button" variant="outline" onClick={() => refetch()}>
            <Trans>Retry</Trans>
          </Button>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && projects.length === 0 && (
        <div className="py-12 text-center text-slate-500">
          <Trans>No projects found.</Trans>
        </div>
      )}

      {/* Content */}
      {!isLoading && !isError && projects.length > 0 && (
        <>
          {viewMode === 'tiles' ? (
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <ProjectTile key={project.id} project={project} />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent>
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="px-4 table-header whitespace-nowrap">
                        <Trans>Project name</Trans>
                      </th>
                      <th className="px-4 table-header whitespace-nowrap">
                        <Trans>Tags</Trans>
                      </th>
                      <th className="px-4 table-header whitespace-nowrap">
                        <Trans>Simulation state</Trans>
                      </th>
                      <th className="px-4 table-header whitespace-nowrap">
                        <Trans>Collisions</Trans>
                      </th>
                      <th className="px-4 table-header whitespace-nowrap">
                        <Trans>Created</Trans>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => {
                      const hasCollisions =
                        project.simulation_result !== null && project.simulation_result.total_collision_count > 0;
                      return (
                        <tr
                          key={project.id}
                          className={cn('hover:bg-slate-50', hasCollisions ? 'border-l-red-500' : 'border-l-slate-200')}
                        >
                          <td className={cn('px-4 py-2 font-medium text-slate-800')}>
                            <Link to={`/projects/${project.id}`} className="hover:underline">
                              {project.name}
                            </Link>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex flex-wrap gap-1">
                              {project.tags.map((tag) => (
                                <Badge
                                  key={tag}
                                  variant="secondary"
                                  className="bg-blue-50 text-blue-600 hover:bg-blue-100"
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <SimulationStateBadge
                              state={project.simulation_state}
                              simulationResult={project.simulation_result}
                            />
                          </td>
                          <td
                            className={cn('px-4 py-2', hasCollisions ? 'font-semibold text-red-600' : 'text-slate-500')}
                          >
                            {project.simulation_result?.total_collision_count != null ? (
                              <Plural
                                value={project.simulation_result.total_collision_count}
                                one="# collision"
                                other="# collisions"
                              />
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-slate-400">{relativeTime(i18n, project.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Pagination – "Showing X–Y of Z" on left, page X/Y center, prev/next right */}
          <div className="flex items-center justify-between pt-3.5">
            <span className="text-slate-400">
              <Trans>Showing</Trans> {rangeStart}–{rangeEnd} <Trans>of</Trans> {total}
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1.5">
                <Button type="button" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                  ← <Trans>Previous</Trans>
                </Button>
                <span className="self-center px-2 text-slate-500">
                  {page} / {totalPages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <Trans>Next</Trans> →
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
